import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../site/auth";
import { useSeo } from "../../site/ui";

/* 라오나봇 로그인 · 회원가입 — 회원 계정은 사이트 전체가 하나라 /api/members 를 그대로 쓴다.
   (I.META 에서 가입한 계정으로 그대로 로그인되고, 이메일 인증도 같은 절차다.) 화면만 라오나봇 것으로 따로 둔다. */

/* 로그인 뒤 돌아갈 곳 — 라오나봇 안의 주소만 받는다 */
export function useNextPath() {
  const location = useLocation();
  const [params] = useSearchParams();
  const v = location.state?.from || params.get("next") || "";
  return typeof v === "string" && /^\/laonabot(\/|$|\?)/.test(v) ? v : "/laonabot/projects";
}

/* 회원 전용 화면 감싸기 — 로그인하지 않았으면 로그인 화면으로 보낸다 */
export function RequireMember({ children }) {
  const { member, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <p className="lb-empty lb-wrap">로그인 정보를 확인하는 중입니다…</p>;
  if (!member) {
    return <Navigate to="/laonabot/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
}

/* 이메일 인증을 마친 회원만 — 연결도 만들기. 서버(/api/laona/tool · /api/laona/modules)도 같은 규칙으로 막는다. */
export function RequireVerified({ children }) {
  const { member } = useAuth();
  if (member && !member.email_verified) return <VerifyNotice />;
  return children;
}

function VerifyNotice() {
  useSeo({ title: "이메일 인증이 필요합니다 — LaonaBot", path: "/laonabot/wiring" });
  const { member, call, refresh } = useAuth();
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState({ kind: "", text: "" });

  // 메일의 링크로 인증하고 이 창으로 돌아오면 저절로 열리게 한다
  const recheck = useCallback(async (manual) => {
    await refresh();
    if (manual) setMsg({ kind: "info", text: "아직 인증이 확인되지 않았습니다. 메일의 링크를 눌렀는지 확인해 주세요." });
  }, [refresh]);
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible") recheck(false); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, [recheck]);

  async function resend() {
    setBusy("resend");
    setMsg({ kind: "", text: "" });
    try {
      const d = await call("/verify/resend", { method: "POST" });
      if (d && d.already) { await refresh(); return; }
      setMsg({ kind: "ok", text: `${member.email} 로 인증 메일을 다시 보냈습니다. 스팸함도 확인해 주세요.` });
    } catch (err) {
      setMsg({ kind: "err", text: err.message });
    } finally {
      setBusy("");
    }
  }

  return (
    <section className="lb-section lb-section--top lb-verify">
      <div className="lb-auth">
        <p className="lb-kicker">VERIFY EMAIL</p>
        <h1>이메일 인증이 필요합니다</h1>
        <p className="lb-auth__lead">
          연결도 만들기는 이메일 인증을 마친 회원만 쓸 수 있습니다.
          가입할 때 <b>{member.email}</b> 로 보낸 인증 메일의 링크를 눌러 주세요.
          인증한 뒤 이 화면으로 돌아오면 바로 열립니다.
        </p>
        {msg.text && <p className={msg.kind === "err" ? "lb-error" : "lb-okmsg"} role="status">{msg.text}</p>}
        <button type="button" className="lb-btn lb-btn--primary lb-btn--block" onClick={() => recheck(true)} disabled={!!busy}>
          인증을 마쳤어요
        </button>
        <button type="button" className="lb-btn lb-btn--block" onClick={resend} disabled={!!busy}>
          {busy === "resend" ? "보내는 중…" : "인증 메일 다시 보내기"}
        </button>
        <p className="lb-auth__note">자료실은 인증 전에도 볼 수 있습니다. <Link to="/laonabot/library">자료실로</Link></p>
      </div>
    </section>
  );
}

export function LaonaLogin() {
  useSeo({ title: "로그인 — LaonaBot", path: "/laonabot/login" });
  const { login, member } = useAuth();
  const navigate = useNavigate();
  const next = useNextPath();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (member) return <Navigate to={next} replace />;

  async function onSubmit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await login(form.email.trim(), form.password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="lb-section lb-section--top">
      <form className="lb-auth" onSubmit={onSubmit} noValidate>
        <p className="lb-kicker">MEMBERS</p>
        <h1>로그인</h1>
        <p className="lb-auth__lead">연결도 만들기와 자료실은 회원 전용입니다.</p>
        <p className="lb-auth__hint">I.META(아이닷메타) 회원이시면 같은 이메일 · 비밀번호로 로그인하세요.</p>

        <label className="lb-field">
          <span>이메일</span>
          <input type="email" required maxLength={200} autoComplete="email"
                 value={form.email} onChange={upd("email")} disabled={busy} />
        </label>
        <label className="lb-field">
          <span>비밀번호</span>
          <input type="password" required maxLength={200} autoComplete="current-password"
                 value={form.password} onChange={upd("password")} disabled={busy} />
        </label>

        {error && <p className="lb-error" role="alert">{error}</p>}

        <button type="submit" className="lb-btn lb-btn--primary lb-btn--block" disabled={busy}>
          {busy ? "로그인 중…" : "로그인"}
        </button>
        <p className="lb-auth__note">
          비밀번호를 잊으셨나요? <a href="/password-reset" target="_blank" rel="noopener">비밀번호 찾기</a>
        </p>
        <div className="lb-auth__switch">
          <span>아직 회원이 아니신가요? 가입은 무료입니다.</span>
          <Link to="/laonabot/signup" state={{ from: next }} className="lb-btn lb-btn--block">회원가입</Link>
        </div>
      </form>
    </section>
  );
}

const EMPTY = { email: "", password: "", password2: "", name: "", phone: "", website: "" };

export function LaonaSignup() {
  useSeo({ title: "회원가입 — LaonaBot", path: "/laonabot/signup" });
  const { signup, member } = useAuth();
  const navigate = useNavigate();
  const next = useNextPath();
  const [form, setForm] = useState(EMPTY);
  const [agree, setAgree] = useState({ terms: false, privacy: false, age: false, marketing: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const tog = (k) => (e) => setAgree((a) => ({ ...a, [k]: e.target.checked }));
  const allRequired = agree.terms && agree.privacy && agree.age;

  /* 가입 직후에는 signup() 이 회원을 먼저 채우므로, 보내는 중(busy)에는 넘기지 않는다 */
  if (member && !done && !busy) return <Navigate to={next} replace />;

  async function onSubmit(e) {
    e.preventDefault();
    if (busy) return;
    if (form.password !== form.password2) return setError("비밀번호가 서로 다릅니다.");
    if (form.password.length < 8) return setError("비밀번호는 8자 이상으로 정해 주세요.");
    if (!allRequired) return setError("필수 항목에 모두 동의하셔야 가입할 수 있습니다.");
    setBusy(true);
    setError("");
    try {
      const data = await signup({
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        phone: form.phone.trim(),
        company: "",
        terms_agree: agree.terms,
        privacy_agree: agree.privacy,
        age_agree: agree.age,
        marketing_agree: agree.marketing,
        website: form.website,
      });
      setDone(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <section className="lb-section lb-section--top">
        <div className="lb-auth">
          <p className="lb-kicker">WELCOME</p>
          <h1>가입이 완료되었습니다</h1>
          <p className="lb-auth__lead">
            {done.verify_mail_sent
              ? "보내 드린 인증 메일의 링크를 눌러 이메일 인증을 마쳐 주세요. 인증을 마쳐야 연결도 만들기를 쓸 수 있습니다. 메일이 보이지 않으면 스팸함을 확인해 주세요."
              : "이메일 인증을 마치면 연결도 만들기를 쓸 수 있습니다. 인증 메일은 다음 화면에서 다시 받을 수 있습니다."}
          </p>
          <button type="button" className="lb-btn lb-btn--primary lb-btn--block" onClick={() => navigate(next, { replace: true })}>
            시작하기
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="lb-section lb-section--top">
      <form className="lb-auth" onSubmit={onSubmit} noValidate>
        <p className="lb-kicker">JOIN</p>
        <h1>회원가입</h1>
        <p className="lb-auth__lead">가입하면 연결도 만들기와 자료실을 무료로 쓸 수 있습니다.</p>

        {/* 라오나봇은 따로 보이게 두되, 계정 · 개인정보를 맡는 곳이 I.META 임은 가입 전에 알린다 */}
        <div className="lb-operator" role="note">
          <b>라오나봇은 I.META(아이닷메타)가 운영합니다.</b>
          <ul>
            <li>가입하면 <b>I.META 회원 계정</b>이 만들어지고, 같은 계정으로 I.META 사이트도 이용할 수 있습니다.</li>
            <li>이미 I.META 회원이시면 새로 가입하지 말고 <Link to="/laonabot/login" state={{ from: next }}>로그인</Link>해 주세요.</li>
            <li>회원 정보는 I.META가 관리하며, 인증 메일도 I.META 이름으로 발송됩니다.</li>
          </ul>
        </div>

        <label className="lb-field">
          <span>이메일 <i>*</i></span>
          <input type="email" required maxLength={200} autoComplete="email"
                 value={form.email} onChange={upd("email")} disabled={busy} />
        </label>
        <div className="lb-field2">
          <label className="lb-field">
            <span>비밀번호 <i>*</i></span>
            <input type="password" required minLength={8} maxLength={200} autoComplete="new-password"
                   value={form.password} onChange={upd("password")} disabled={busy} />
          </label>
          <label className="lb-field">
            <span>비밀번호 확인 <i>*</i></span>
            <input type="password" required minLength={8} maxLength={200} autoComplete="new-password"
                   value={form.password2} onChange={upd("password2")} disabled={busy} />
          </label>
        </div>
        <div className="lb-field2">
          <label className="lb-field">
            <span>이름 <i>*</i></span>
            <input type="text" required maxLength={60} autoComplete="name"
                   value={form.name} onChange={upd("name")} disabled={busy} />
          </label>
          <label className="lb-field">
            <span>연락처 <em>(선택)</em></span>
            <input type="tel" maxLength={40} autoComplete="tel"
                   value={form.phone} onChange={upd("phone")} disabled={busy} />
          </label>
        </div>

        {/* 봇 트랩 */}
        <div className="lb-trap" aria-hidden="true">
          <label>홈페이지<input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={upd("website")} /></label>
        </div>

        <div className="lb-agree">
          <label><input type="checkbox" checked={agree.terms} onChange={tog("terms")} disabled={busy} />
            <span>I.META <a href="/terms" target="_blank" rel="noopener">이용약관</a>에 동의합니다. <i>*</i></span></label>
          <label><input type="checkbox" checked={agree.privacy} onChange={tog("privacy")} disabled={busy} />
            <span>I.META의 <a href="/privacy" target="_blank" rel="noopener">개인정보 수집·이용</a>에 동의합니다. <i>*</i></span></label>
          <label><input type="checkbox" checked={agree.age} onChange={tog("age")} disabled={busy} />
            <span>만 14세 이상입니다. <i>*</i></span></label>
          <label><input type="checkbox" checked={agree.marketing} onChange={tog("marketing")} disabled={busy} />
            <span>새 자료 · 소식 메일을 받겠습니다. <em>(선택)</em></span></label>
        </div>

        {error && <p className="lb-error" role="alert">{error}</p>}

        <button type="submit" className="lb-btn lb-btn--primary lb-btn--block" disabled={busy}>
          {busy ? "가입 중…" : "가입하기"}
        </button>
        <p className="lb-auth__note">
          이미 계정이 있으신가요? <Link to="/laonabot/login" state={{ from: next }}>로그인</Link>
        </p>
      </form>
    </section>
  );
}
